import {
  KB_DUMMY_PATHNAME,
  KB_DUMMY_PATHNAME_SPLIT_POINT,
} from '../_components/kbHistoryConstants';

const KB_DUMMY_PREFIX = `${KB_DUMMY_PATHNAME}${KB_DUMMY_PATHNAME_SPLIT_POINT}`;

/**
 * KbHistoryStack이 기록한 URL의 pathname에서 인코딩된 searchParam(`kbpostparam__key__value`)
 * 더미 segment를 걷어내고 원래 pathname을 복원한다.
 */
const restoreOriginalPathname = (pathnameWithDummy: string): string => {
  const segments = pathnameWithDummy
    .split('/')
    .filter((seg) => !seg.startsWith(KB_DUMMY_PREFIX));

  return segments.join('/') || '/';
};

/**
 * `/community/policyInfo/title--uuid` 처럼 `<slug>--<id>` 패턴인 마지막 segment에서
 * id 부분만 추출. delimiter가 없으면 전체 segment를 canonical id로 본다.
 *
 * - 백엔드 sitemap과 동일한 `--` delimiter 규약(`detailSlug.ts`의 `buildDetailSlug`)에 의존.
 */
const extractCanonicalKey = (segment: string): string => {
  if (!segment) return '';
  const parts = segment.split('--');

  return parts[parts.length - 1] ?? segment;
};

/**
 * 직전 URL과 신규 pathname이 동일 페이지의 slug 정규화(`permanentRedirect`) 케이스인지 판단.
 *
 * 상세 페이지(`generateMetadata`에서 비정규 slug → canonical slug로 redirect)에서
 * 두 pathname이 한 번에 KB 히스토리에 누적되어 뒤로가기가 즉시 redirect 루프로 막히는 문제를
 * 방지하기 위해 사용. 이 경우 push 대신 last entry 교체로 처리한다.
 *
 * 판단 기준:
 * 1. 두 pathname의 segment 개수가 동일
 * 2. 마지막 segment를 제외한 부모 경로가 정확히 일치
 * 3. 마지막 segment의 canonical id(`--` 뒤 부분)가 동일하고 비어있지 않음
 */
export const isCanonicalSlugRedirect = (
  previousUrl: string | undefined,
  nextPathname: string,
): boolean => {
  if (!previousUrl || !nextPathname) return false;

  let prevPathname: string;
  try {
    prevPathname = new URL(previousUrl).pathname;
  } catch {
    return false;
  }

  const prevSegments = restoreOriginalPathname(prevPathname).split('/');
  const nextSegments = nextPathname.split('/');

  if (prevSegments.length !== nextSegments.length) return false;
  if (prevSegments.length < 2) return false;

  const prevLast = prevSegments[prevSegments.length - 1];
  const nextLast = nextSegments[nextSegments.length - 1];

  const prevParent = prevSegments.slice(0, -1).join('/');
  const nextParent = nextSegments.slice(0, -1).join('/');
  if (prevParent !== nextParent) return false;

  const prevKey = extractCanonicalKey(prevLast);
  const nextKey = extractCanonicalKey(nextLast);
  if (!prevKey || !nextKey) return false;

  return prevKey === nextKey && prevLast !== nextLast;
};
